/**
 * AdminDashboardPage - Simplified admin dashboard within Advisor Panel
 *
 * NOTE: This is a LEGACY component. Full admin functionality is available in the
 * dedicated Admin Panel at /apps/admin-panel. This page provides quick access to
 * basic stats for admins who need to manage advisors without switching applications.
 *
 * TODO: Consider removing this in favor of redirecting to Admin Panel
 * @see apps/admin-panel/ for full admin functionality
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/shared/lib/api";
import { Card, CardContent, CardHeader, CardTitle, Button } from "@/shared/components/ui";
import { formatDateTime } from "@/shared/lib/utils";
import { Users, FileText, MessageSquare, CreditCard, ArrowRight, Loader2, AlertCircle, ExternalLink } from "lucide-react";
import type { AdminStats } from "@/shared/types";

export function AdminDashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: () => api.getStats(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const stats = data as AdminStats | undefined;

  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="w-12 h-12 text-error-500 mx-auto mb-4" />
        <p className="text-gray-600">Nie udało się pobrać statystyk</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner - Link to full Admin Panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ExternalLink className="w-5 h-5 text-blue-600" />
          <div>
            <p className="text-sm font-medium text-blue-800">
              Pełny Panel Administratora
            </p>
            <p className="text-xs text-blue-600">
              Zarządzanie ticketami, płatnościami, rolami i więcej
            </p>
          </div>
        </div>
        <a
          href="/admin"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          Otwórz Admin Panel <ArrowRight className="w-4 h-4" />
        </a>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Panel Administratora</h1>
          <p className="text-gray-600 mt-1">Przegląd systemu (widok uproszczony)</p>
        </div>
        {stats && (
          <p className="text-sm text-gray-500">
            Ostatnia aktualizacja: {formatDateTime(stats.timestamp)}
          </p>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Doradcy</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stats?.totals.advisors || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-success-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Leady</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stats?.totals.leads || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-warning-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Sesje</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stats?.totals.sessions || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Oferty</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stats?.totals.offers || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Advisors by Status */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Doradcy wg statusu</CardTitle>
                <Link to="/admin/advisors">
                  <Button variant="ghost" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
                    Zarządzaj
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-success-500" />
                      <span className="text-sm text-gray-600">Aktywni</span>
                    </div>
                    <span className="font-medium">
                      {stats?.advisors.byStatus.active || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-warning-500" />
                      <span className="text-sm text-gray-600">Oczekujący</span>
                    </div>
                    <span className="font-medium">
                      {stats?.advisors.byStatus.pending || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-error-500" />
                      <span className="text-sm text-gray-600">Zawieszeni</span>
                    </div>
                    <span className="font-medium">
                      {stats?.advisors.byStatus.suspended || 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Leads by Status */}
            <Card>
              <CardHeader>
                <CardTitle>Leady wg statusu</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-success-500" />
                      <span className="text-sm text-gray-600">Nowe (dostępne)</span>
                    </div>
                    <span className="font-medium">
                      {stats?.leads.available || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-warning-500" />
                      <span className="text-sm text-gray-600">Zarezerwowane</span>
                    </div>
                    <span className="font-medium">
                      {stats?.leads.reserved || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-400" />
                      <span className="text-sm text-gray-600">Wygasłe</span>
                    </div>
                    <span className="font-medium">
                      {stats?.leads.byStatus.expired || 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sessions by Status */}
            <Card>
              <CardHeader>
                <CardTitle>Sesje wg statusu</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-primary-500" />
                      <span className="text-sm text-gray-600">Aktywne</span>
                    </div>
                    <span className="font-medium">
                      {stats?.sessions.active || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-success-500" />
                      <span className="text-sm text-gray-600">Zakończone</span>
                    </div>
                    <span className="font-medium">
                      {stats?.sessions.completed || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-400" />
                      <span className="text-sm text-gray-600">Wygasłe</span>
                    </div>
                    <span className="font-medium">
                      {stats?.sessions.byStatus.expired || 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
